import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export interface TelegramWebApp {
  ready: () => void;
  close: () => void;
  expand: () => void;
  setHeaderTitle: (title: string) => void;
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    enable: () => void;
    disable: () => void;
    isVisible: boolean;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    isVisible: boolean;
  };
  themeParams: TelegramThemeParams;
  colorScheme: 'light' | 'dark';
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name: string; last_name?: string; username?: string } };
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  showPopup: (params: { title?: string; message: string; buttons?: { type: string; text: string }[] }, callback?: (buttonId: string) => void) => void;
  showDialog: (params: { title?: string; message: string }) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class TelegramService {
  private isBrowser: boolean;
  private webApp: TelegramWebApp | null = null;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser && window.Telegram?.WebApp) {
      this.webApp = window.Telegram.WebApp;
      this.webApp.ready();
      this.webApp.expand();
    }
  }

  get isTelegram(): boolean {
    return this.webApp !== null;
  }

  get themeParams(): TelegramThemeParams {
    return this.webApp?.themeParams || {};
  }

  get colorScheme(): 'light' | 'dark' {
    return this.webApp?.colorScheme || 'light';
  }

  get initData(): object {
    const params = new URLSearchParams(this.webApp?.initData || '')
    const result: Record<string, any> = {}
    params.forEach((val, key) => {
      result[key] = val
    })
    return result
  }

  close(): void {
    this.webApp?.close();
  }

  setHeaderTitle(title: string): void {
    this.webApp?.setHeaderTitle(title);
  }

  showMainButton(text: string, onClick: () => void): void {
    if (this.webApp) {
      this.webApp.MainButton.text = text;
      this.webApp.MainButton.onClick(onClick);
      this.webApp.MainButton.show();
    }
  }

  hideMainButton(): void {
    this.webApp?.MainButton.hide();
  }

  showBackButton(onClick: () => void): void {
    if (this.webApp) {
      this.webApp.BackButton.onClick(onClick);
      this.webApp.BackButton.show();
    }
  }

  hideBackButton(): void {
    this.webApp?.BackButton.hide();
  }

  showPopup(params: { title?: string; message: string; buttons?: { type: string; text: string }[] }, callback?: (buttonId: string) => void): void {
    this.webApp?.showPopup(params, callback);
  }
}